import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Breadcrumb, Form, Typography, Row, Col } from 'antd'
import { useManualQuery } from 'graphql-hooks'

import { getCompanyBillingInfo } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'

import { useAppDispatch } from '../../../store/hooks'
import { setAuthCompanyPlan } from '../../../store/auth-company-slice'
import { ProductService } from '@vacationtracker/shared/data/stripe/product-service'

import IntlMessages from '../../../util/IntlMessages'
import CircularProgress from '../../../components/circular-progress'
import PricePlanCard from '../../../components/price-plan-card'
import { SubscriptionPeriod, USER_PLAN_LIMIT } from '@vacationtracker/shared/types/billing'

import { IGetCompanyBillingInfo, IGetCompanyBillingInfoData } from '../../../types/company'
import { FrontendUrls } from '../../../types/urls'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'
import { ADPMarketplaceBtn, goToADPMarketplace } from '../../../components/adp/marketplace-btn'
import { IPrice } from '@vacationtracker/shared/data/stripe/prices'

const { Title, Paragraph } = Typography

const ADPBillingPage = () => {
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()

  const productService = new ProductService(process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prod' : 'test')
  const currentPrices = productService.getCurrentPrices()
  const [isLoadingBillingInfo, setIsLoadingBillingInfo] = useState(true)
  const [billing, setBilling] = useState<IGetCompanyBillingInfo | null>(null)
  const [seats, setSeats] = useState<number>(USER_PLAN_LIMIT)

  const [ getCompanyBillingInfoQuery ] = useManualQuery<IGetCompanyBillingInfoData>(getCompanyBillingInfo)

  useEffect(() => {
    fetchBilling()
  }, [])

  const fetchBilling = async () => {
    try {
      const response = await getCompanyBillingInfoQuery()
      if (!response.data || response.error) throw response.error

      dispatch(setAuthCompanyPlan(response.data.getCompany.billing.subscriptionPlan))
      setBilling({
        ...response?.data?.getCompany,
        billing: {
          ...response?.data?.getCompany?.billing,
        },
      })
      setSeats(response?.data?.getCompany?.billing?.seats as number)
      setIsLoadingBillingInfo(false)
    } catch (error) {
      setIsLoadingBillingInfo(false)
    }
  }

  const layout = {
    labelCol: {
      md: { span: 4 },
      sm: { span: 24 },
      xs: { span: 24 },
    },
    wrapperCol: { span: 24 },
  }

  return (
    <div className='main-content'>
      <div className="main-content-header">
        <div className="main-content-header-title">
          <span><IntlMessages id="sidebar.billing" /></span>
        </div>
        <div className="main-content-header-breadcrumb">
          <Breadcrumb
            items={[
              {
                title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
              },
              {
                title: <IntlMessages id="sidebar.section.workspaceSettingsLowercase" />,
              },
              {
                title: <IntlMessages id="sidebar.billing" />,
              },
            ]}
          />
        </div>
      </div>

      <div className="main-content-body">
        <div className="billing-body">
          {isLoadingBillingInfo ?
            <CircularProgress /> :
            <Form
              form={form}
              layout="horizontal"
              name="adpForm"
              onFinish={() => {
                logger.debug('submitting billing...')
              }}
              {...layout}
            >

              <Title level={4}>
                <IntlMessages id="billing.pricingPlansTitle" />
              </Title>

              <Row justify='center' style={{ marginBottom: 10 }} gutter={{ lg: 16, xl: seats > 300 ? 4 : 8 }}>
                <Col xxl={{ span: 7, offset: 0 }} xl={8} lg={14} md={14} sm={24} xs={24} style={{ display: 'flex' }}>
                  <PricePlanCard
                    plan='Core'
                    price={currentPrices.find(p => p.plan === 'Core' && p.period === 'annual') as IPrice}
                    totalUsers={seats}
                    currentPeriod={billing?.billing?.subscriptionPeriod as SubscriptionPeriod}
                    newPeriod={billing?.billing?.subscriptionPeriod as SubscriptionPeriod}
                    currentPlan={billing?.billing?.subscriptionPlan as SubscriptionPlan}
                    showRevertingInfo={true}
                    isSignup={false}
                    onSelectPLan={goToADPMarketplace}
                    isTrialPeriod={false}
                    billingType={billing?.billing.billingType}
                    forceDisable={false}
                  />
                </Col>
                <Col xxl={7} xl={8} lg={14} md={14} sm={24} xs={24} style={{ display: 'flex' }}>
                  <PricePlanCard
                    plan='Complete'
                    price={currentPrices.find(p => p.plan === 'Complete' && p.period === 'annual') as IPrice}
                    totalUsers={seats}
                    currentPeriod={billing?.billing?.subscriptionPeriod as SubscriptionPeriod}
                    newPeriod={billing?.billing?.subscriptionPeriod as SubscriptionPeriod}
                    currentPlan={billing?.billing?.subscriptionPlan as SubscriptionPlan}
                    showRevertingInfo={true}
                    isSignup={false}
                    onSelectPLan={goToADPMarketplace}
                    isTrialPeriod={false}
                    billingType={billing?.billing.billingType}
                    forceDisable={false}
                  />
                </Col>
              </Row>
              <Paragraph>
                <Title level={4}>Licences: {seats}</Title>
              </Paragraph>
              <Paragraph>
                <Title level={4}>
                  <IntlMessages id="components.billingInfo.title" />
                  <ADPMarketplaceBtn />
                </Title>
              </Paragraph>
            </Form>
          }
        </div>
      </div>

    </div>
  )
}

export default ADPBillingPage
